import Phaser from 'phaser';
import type { CharacterStatsData, InventorySlotData } from '@shared/types/messages';
import { CHARACTER_CLASSES, CHARACTER_RACES, getRaceInnateModifiers, type CharacterRaceId } from '@shared/types/character';
import { describeModifiers, formatModifierValue } from '@shared/data/modifierDisplay';
import { getKingdomLabel, type KingdomId } from '@shared/data/kingdoms';
import { Direction } from '@shared/types/entity';
import { EquipmentSlot, GAME_FONT_FAMILY } from '@shared/constants';
import { createCloseButton, getIconTexture, getStatIconName } from '../../sprites/IconAtlases';
import { getItemTemplate } from '@shared/data/items';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getHelmetSpriteKey, getHelmetFrameForDirection } from '../../sprites/HelmetSpriteManager';
import { getBootSpriteKey, getBootFrameForDirection } from '../../sprites/BootSpriteManager';
import { getWeaponSpriteKey, getWeaponIdleFrame } from '../../sprites/WeaponSpriteManager';
import { getShieldSpriteKey, getShieldIdleFrame } from '../../sprites/ShieldSpriteManager';
import { ItemTooltip } from './ItemTooltip';

/** IDs visuales del player local para componer el preview del equipment display. */
export type LocalPlayerVisualIds = {
  bodyId: number;
  headId: number;
  helmId: number;
  bootId: number;
  weaponId: number;
  shieldId: number;
};

/**
 * Geometría de composición del sprite del personaje (debe coincidir con la usada
 * por `GameScene.addOrUpdatePlayer` — ver `GameScene.ts:11320-11420`).
 */
const CHAR_PREVIEW = {
  BODY_FRAME_HEIGHT: 64,
  HEAD_FRAME_HEIGHT: 30,
  HEAD_OFFSET_FROM_BODY_BOTTOM: 27,
  SHIELD_OFFSET_X: 0,
  WEAPON_OFFSET_X: -1,
  HELM_OFFSET_Y: -8,
} as const;

/** Y offset (relativo al top del panel) donde arranca el contenido (debajo del título). */
const CONTENT_TOP_OFFSET = 36;

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

type CharacterInfoPanelCallbacks = {
  onMove(x: number, y: number): void;
  /** Right-click sobre un slot equipado → desequipar el item del slot dado. */
  onUnequip(equipSlot: EquipmentSlot): void;
  /** Devuelve los items actualmente equipados, indexados por slot. */
  getEquippedItems(): Map<EquipmentSlot, InventorySlotData>;
  /** Devuelve los IDs visuales del player local para el preview. */
  getLocalPlayerVisualIds(): LocalPlayerVisualIds | null;
};

type CharacterInfoPanelOptions = {
  initialX: number;
  initialY: number;
};

/** Legacy: el panel tenía tabs (general/skills/coming) pero ahora solo queda 'general'. */
type AccordionSection = 'general';

type CharacterInfoPanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  /** Sprite del ícono `character_info` a la izquierda del título. */
  titleIcon: Phaser.GameObjects.Sprite | null;
  closeButton: Phaser.GameObjects.Sprite;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
};

type ScrollContainer = {
  container: Phaser.GameObjects.Container;
  scrollContent: Phaser.GameObjects.Container;
  scrollY: number;
  maxScrollY: number;
  box: Phaser.GameObjects.Rectangle;
  /** Graphics invisible que define el área visible del scrollContent (clipping). */
  maskGraphics: Phaser.GameObjects.Graphics;
  /** Track del scrollbar (arte dorado `scroll_bar`). */
  scrollbarTrack: Phaser.GameObjects.Image;
  /** Thumb del scrollbar (arte dorado `scroll_handler`, arrastrable). */
  scrollbarThumb: Phaser.GameObjects.Image;
};

export class CharacterInfoPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: CharacterInfoPanelCallbacks;
  
  private panel: CharacterInfoPanelElements | null = null;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  /** True mientras se arrastra el thumb del scrollbar (mantiene el sprite hover). */
  private draggingScrollbar = false;
  
  // Contenedor de la sección General (único; no hay tabs visibles).
  private sectionContainers: Map<AccordionSection, Phaser.GameObjects.Container> = new Map();
  
  // Tab General: columna izquierda (equipment + char preview, sin scroll)
  // y columna derecha (stats avanzados, scrolleable).
  private leftPaneContainer: Phaser.GameObjects.Container | null = null;
  private rightScrollContainer: ScrollContainer | null = null;
  /** Bounds en pantalla del rightScrollContainer para el wheel handler. */
  private rightScrollBounds: { x: number; y: number; w: number; h: number } | null = null;

  // Datos de estadísticas
  private statsData: CharacterStatsData | null = null;
  /** True cuando `createHeader` corrió con `statsData != null` y dejó los
   *  textos del header (nombre, nivel·raza·clase, subclase, ascendencia) en el
   *  left pane. Si es false, hay que reconstruir el left pane (no sólo la
   *  columna derecha) la próxima vez que llegan datos o se abre el panel. */
  private headerHydrated = false;

  // Tooltip de raza (string-list, render custom). Distinto del ItemTooltip.
  private currentTooltip: Phaser.GameObjects.Container | null = null;
  private currentTooltipContent: string[] | null = null;

  // Tooltip reusable para items equipados (mismo que MerchantPanel/InventoryPanel).
  private itemTooltip: ItemTooltip | null = null;

  // Equipment display: refs para refrescar sin re-armar toda la sección.
  // Cada slot guarda sus gameObjects y el item actualmente mostrado.
  private equipmentSlots: Map<EquipmentSlot, {
    rect: Phaser.GameObjects.Rectangle;
    icon: Phaser.GameObjects.Sprite;
    placeholder: Phaser.GameObjects.Sprite | null;
    currentItem: InventorySlotData | null;
  }> = new Map();
  // Sprites del char preview (re-creados en refresh cuando cambian visualIds).
  private characterPreviewContainer: Phaser.GameObjects.Container | null = null;

  constructor(scene: Phaser.Scene, callbacks: CharacterInfoPanelCallbacks, options: CharacterInfoPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
    
    // Wheel handler: solo la columna derecha (stats avanzados) scrolllea.
    // Usa los bounds calculados en createGeneralContent.
    this.scene.input.on('wheel', (pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      if (!this.isOpen) return;
      const b = this.rightScrollBounds;
      if (!b || !this.rightScrollContainer) return;
      if (pointer.x < b.x || pointer.x > b.x + b.w || pointer.y < b.y || pointer.y > b.y + b.h) return;
      this.scrollContainer(this.rightScrollContainer, deltaY * 0.5);
    });
    
    // Ocultar tooltip cuando el mouse sale del panel (ya no es necesario con hover)
    // El tooltip se oculta automáticamente cuando el pointer sale del área interactiva
  }

  private scrollContainer(scrollContainer: ScrollContainer, deltaY: number): void {
    const oldScrollY = scrollContainer.scrollY;
    scrollContainer.scrollY = Phaser.Math.Clamp(
      scrollContainer.scrollY + deltaY,
      0,
      scrollContainer.maxScrollY
    );
    const scrollDelta = scrollContainer.scrollY - oldScrollY;
    scrollContainer.scrollContent.y -= scrollDelta;
    this.refreshScrollbar(scrollContainer);
  }

  toggle(): void {
    if (!this.panel) return;
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (!this.panel) return;
    this.isOpen = true;
    this.setVisible(true);
    // Si los stats ya llegaron mientras el panel estaba cerrado, el header
    // inicial se construyó vacío. Forzar rebuild completo para hidratarlo.
    if (this.statsData && !this.headerHydrated) {
      this.updateUI();
    }
  }

  close(): void {
    if (!this.panel) return;
    this.isOpen = false;
    this.setVisible(false);
    this.hideTooltip();
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  updateStats(stats: CharacterStatsData): void {
    this.statsData = stats;
    if (this.isOpen) {
      // Si el header del left pane todavía no se hidrató (porque la primera
      // build corrió con statsData=null), hacer rebuild completo. Después
      // basta con la columna derecha.
      if (!this.headerHydrated) {
        this.updateUI();
      } else {
        // Sólo re-renderizar la columna de stats (right column). El equipment
        // + char preview se mantienen — esos se actualizan por su propio
        // callback (`refreshEquippedSlots` / `refreshCharacterPreview`) cuando
        // llega el INVENTORY_SYNC y el delta del schema, respectivamente.
        this.rebuildStatsColumnOnly();
      }
    }
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(initialX: number, initialY: number): void {
    const { width, height } = this.scene.scale;
    const panelWidth = 600;
    const panelHeight = 500;
    const panelX = initialX ?? width / 2;
    const panelY = initialY ?? height / 2;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.95)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0x666666)
      .setDepth(850)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive();

    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 16, 'Información del Personaje', withGameFont({
        fontSize: '28px',
        color: '#ffffff',
        fontStyle: 'bold',
      }))
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(851)
      .setScrollFactor(0);

    // Sprite `character_info` a la izquierda del título (decorativo).
    let titleIcon: Phaser.GameObjects.Sprite | null = null;
    const charInfoIconTex = getIconTexture('character_info');
    if (charInfoIconTex && this.scene.textures.exists(charInfoIconTex.textureKey)) {
      const ICON = 18;
      const GAP = 8;
      titleIcon = this.scene.add
        .sprite(panelX - title.width / 2 - GAP - ICON / 2, title.y,
          charInfoIconTex.textureKey, charInfoIconTex.frame)
        .setOrigin(0.5)
        .setDisplaySize(ICON, ICON)
        .setDepth(851)
        .setScrollFactor(0);
    }

    // Botón de cerrar
    const closeButton = createCloseButton(this.scene, panelX + panelWidth / 2 - 14, panelY - panelHeight / 2 + 14, 16)!
      .setOrigin(0.5, 0)
      .setDepth(852)
      .setAlpha(0.75);

    closeButton.on('pointerover', () => closeButton.setAlpha(1));
    closeButton.on('pointerout', () => closeButton.setAlpha(0.75));

    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    // Crear accordion
    this.createAccordion(panelX, panelY, panelWidth, panelHeight);

    // Drag handler en el título
    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.panel) return;
      this.isDragging = true;
      this.dragOffsetX = pointer.x - this.panel!.panelX;
      this.dragOffsetY = pointer.y - this.panel!.panelY;
      pointer.event.stopPropagation();
    });

    this.panel = {
      bg,
      title,
      titleIcon,
      closeButton,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
    };
  }

  private createAccordion(panelX: number, panelY: number, panelWidth: number, panelHeight: number): void {
    // El panel ya no tiene tabs visibles — solo existe la sección "General".
    // Se mantiene el sectionContainers map por compat con el resto del código.
    const contentY = panelY - panelHeight / 2 + CONTENT_TOP_OFFSET;
    const container = this.scene.add.container(panelX, contentY);
    container.setDepth(851);
    container.setScrollFactor(0);
    container.setVisible(true);
    this.sectionContainers.set('general', container);

    // Crear contenido de la sección General (única).
    this.createGeneralSection(panelX, panelY, panelWidth, panelHeight);
  }

  private createScrollContainer(
    parentContainer: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number
  ): ScrollContainer {
    // Crear el contenedor principal en coordenadas relativas al parent
    const container = this.scene.add.container(x, y);
    container.setDepth(851);
    container.setScrollFactor(0);
    parentContainer.add(container);

    // Crear el box (fondo con borde) en coordenadas locales del container
    const box = this.scene.add
      .rectangle(0, 0, width, height, 0x1a1a1a, 0.8)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x444444)
      .setDepth(851)
      .setScrollFactor(0);
    container.add(box);

    // Crear el contenedor de contenido scrolleable en coordenadas locales
    const scrollContent = this.scene.add.container(0, 0);
    scrollContent.setDepth(851);
    scrollContent.setScrollFactor(0);
    container.add(scrollContent);

    // Máscara: clipea el scrollContent al área del box. El Graphics vive en
    // coordenadas world (no es hijo de ningún container), por eso se reposiciona
    // manualmente en move() y al crear el panel.
    const wt = container.getWorldTransformMatrix();
    const maskGraphics = this.scene.add.graphics();
    maskGraphics.setScrollFactor(0);
    maskGraphics.setVisible(false); // Solo se usa como mask, no se renderiza.
    maskGraphics.fillStyle(0xffffff);
    maskGraphics.fillRect(wt.tx, wt.ty, width, height);
    scrollContent.setMask(maskGraphics.createGeometryMask());

    // Scrollbar dorado (track + thumb arrastrable). Borde derecho del box.
    // Arte: scroll_bar (track) + scroll_handler (thumb). Ver docs/UI_SKINNING.md.
    const SB_WIDTH = 10;
    const sbX = width - SB_WIDTH - 2;
    const scrollbarTrack = this.scene.add
      .image(sbX, 0, 'scroll_bar')
      .setOrigin(0, 0)
      .setDisplaySize(SB_WIDTH, height)
      .setDepth(853)
      .setScrollFactor(0);
    container.add(scrollbarTrack);
    const scrollbarThumb = this.scene.add
      .image(sbX, 0, 'scroll_handler')
      .setOrigin(0, 0)
      .setDisplaySize(SB_WIDTH, 40)
      .setDepth(854)
      .setScrollFactor(0);
    container.add(scrollbarThumb);

    const scrollContainer: ScrollContainer = {
      container,
      scrollContent,
      scrollY: 0,
      maxScrollY: 0,
      box,
      maskGraphics,
      scrollbarTrack,
      scrollbarThumb,
    };

    // Drag del thumb: arrastrar mueve el scroll proporcionalmente. Hover hace swap
    // al sprite resaltado. `setTexture` resetea la escala de la Image, por eso el
    // swap preserva el displaySize actual (ancho fijo × alto del thumb).
    const swapThumb = (key: string): void => {
      const w = scrollbarThumb.displayWidth;
      const h = scrollbarThumb.displayHeight;
      scrollbarThumb.setTexture(key);
      scrollbarThumb.setDisplaySize(w, h);
    };
    scrollbarThumb.setInteractive({ useHandCursor: true, draggable: true });
    scrollbarThumb.on('pointerover', () => swapThumb('scroll_handler_hover'));
    scrollbarThumb.on('pointerout', () => {
      if (!this.draggingScrollbar) swapThumb('scroll_handler');
    });
    let dragStartPointerY = 0;
    let dragStartScrollY = 0;
    scrollbarThumb.on('dragstart', (pointer: Phaser.Input.Pointer) => {
      this.draggingScrollbar = true;
      dragStartPointerY = pointer.y;
      dragStartScrollY = scrollContainer.scrollY;
      swapThumb('scroll_handler_hover');
    });
    scrollbarThumb.on('drag', (pointer: Phaser.Input.Pointer) => {
      const thumbH = scrollbarThumb.displayHeight;
      const thumbMaxY = scrollContainer.box.height - thumbH;
      if (thumbMaxY <= 0 || scrollContainer.maxScrollY <= 0) return;
      const dy = pointer.y - dragStartPointerY;
      const targetScrollY = dragStartScrollY + (dy / thumbMaxY) * scrollContainer.maxScrollY;
      this.setScrollYAbsolute(scrollContainer, targetScrollY);
    });
    scrollbarThumb.on('dragend', () => {
      this.draggingScrollbar = false;
      swapThumb('scroll_handler');
    });

    return scrollContainer;
  }

  /** Fija el scroll en un valor absoluto (clampeado) y actualiza contenido + thumb. */
  private setScrollYAbsolute(sc: ScrollContainer, scrollY: number): void {
    const clamped = Phaser.Math.Clamp(scrollY, 0, sc.maxScrollY);
    sc.scrollContent.y -= clamped - sc.scrollY;
    sc.scrollY = clamped;
    this.refreshScrollbar(sc);
  }

  /**
   * Recalcula tamaño y posición del thumb del scrollbar y oculta el scrollbar
   * cuando no hay overflow (`maxScrollY === 0`).
   */
  private refreshScrollbar(sc: ScrollContainer): void {
    if (sc.maxScrollY <= 0) {
      sc.scrollbarTrack.setVisible(false);
      sc.scrollbarThumb.setVisible(false);
      return;
    }
    sc.scrollbarTrack.setVisible(true);
    sc.scrollbarThumb.setVisible(true);
    const trackH = sc.box.height;
    const contentH = trackH + sc.maxScrollY;
    const thumbH = Math.max(24, (trackH / contentH) * trackH);
    const thumbMaxY = trackH - thumbH;
    const thumbY = (sc.scrollY / sc.maxScrollY) * thumbMaxY;
    sc.scrollbarThumb.setDisplaySize(sc.scrollbarThumb.displayWidth, thumbH);
    sc.scrollbarThumb.setPosition(sc.scrollbarThumb.x, thumbY);
  }

  /**
   * Recalcula el rect del Graphics que sirve de máscara del scrollContent,
   * basado en la posición world actual del container. Llamar después de mover
   * el panel para que el clipping siga al panel.
   */
  private refreshScrollMask(sc: ScrollContainer): void {
    const wt = sc.container.getWorldTransformMatrix();
    sc.maskGraphics.clear();
    sc.maskGraphics.fillStyle(0xffffff);
    sc.maskGraphics.fillRect(wt.tx, wt.ty, sc.box.width, sc.box.height);
  }

  private createGeneralSection(panelX: number, panelY: number, panelWidth: number, panelHeight: number): void {
    const container = this.sectionContainers.get('general');
    if (!container) return;

    const contentWidth = panelWidth - 40;
    const startX = -panelWidth / 2 + 20;

    const COL_GAP = 20;
    const colWidth = (contentWidth - COL_GAP) / 2;

    // Altura disponible debajo del título.
    const contentStartScreenY = panelY - panelHeight / 2 + CONTENT_TOP_OFFSET;
    const colHeight = panelHeight - CONTENT_TOP_OFFSET - 20;

    // ── Izquierda: cabecera arriba + equipment + char preview, sin scroll ──
    this.leftPaneContainer = this.scene.add.container(startX, 0);
    this.leftPaneContainer.setDepth(851);
    this.leftPaneContainer.setScrollFactor(0);
    container.add(this.leftPaneContainer);
    const leftCx = colWidth / 2;
    const headerHeight = this.createHeader(this.leftPaneContainer, colWidth);
    this.buildEquipmentDisplay(this.leftPaneContainer, leftCx, headerHeight, colWidth);

    // ── Derecha: stats avanzados, scrolleable, desde arriba de todo ──
    const rightX = startX + colWidth + COL_GAP;
    this.rightScrollContainer = this.createScrollContainer(container, rightX, 0, colWidth, colHeight);
    this.buildStatsColumn(this.rightScrollContainer.scrollContent, colWidth);

    // Refrescar la máscara con la posición world actual del container
    // (al construirse, el container puede no haber resuelto su transform).
    this.refreshScrollMask(this.rightScrollContainer);

    // Bounds en pantalla para el wheel handler.
    this.rightScrollBounds = {
      x: panelX + rightX,
      y: contentStartScreenY,
      w: colWidth,
      h: colHeight,
    };

    this.updateScrollLimits();
  }

  private updateScrollLimits(): void {
    const getContentHeight = (scrollContent: Phaser.GameObjects.Container): number => {
      if (scrollContent.list.length === 0) return 0;

      let minY = Infinity;
      let maxY = -Infinity;

      scrollContent.list.forEach((child) => {
        const childY = (child as any).y ?? 0;
        const childHeight = (child as any).height ?? 0;
        minY = Math.min(minY, childY);
        maxY = Math.max(maxY, childY + childHeight);
      });

      return maxY - minY + 20;
    };

    if (this.rightScrollContainer) {
      const contentHeight = getContentHeight(this.rightScrollContainer.scrollContent);
      const scrollHeight = this.rightScrollContainer.box.height;
      this.rightScrollContainer.maxScrollY = Math.max(0, contentHeight - scrollHeight);
      this.refreshScrollbar(this.rightScrollContainer);
    }
  }

  /**
   * Arma la cabecera (nombre, nivel · raza · clase, subclase, ascendencia, HR).
   * Devuelve la Y final (= altura usada), para que el caller sepa dónde arrancar
   * las columnas de abajo.
   */
  private createHeader(container: Phaser.GameObjects.Container, width: number): number {
    if (!this.statsData) {
      this.headerHydrated = false;
      return 0;
    }
    this.headerHydrated = true;

    // Reset refs de equipment (se rearman cuando se construya la columna izq).
    this.equipmentSlots.clear();
    this.characterPreviewContainer = null;

    const cx = width / 2;
    let y = 10;

    // ── Nombre ──
    const nameText = this.scene.add
      .text(cx, y, this.statsData.name, withGameFont({
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#ffffff',
      }))
      .setOrigin(0.5, 0)
      .setDepth(851)
      .setScrollFactor(0);
    container.add(nameText);
    y += 44;

    // ── Nivel · Raza · Clase (línea única, baja prioridad visual) ──
    const level = this.statsData.level ?? 1;
    const raceClass = `Nv ${level} · ${this.statsData.race} · ${this.statsData.class}`;
    const raceClassText = this.scene.add
      .text(cx, y, raceClass, withGameFont({
        fontSize: '22px',
        color: '#999999',
      }))
      .setOrigin(0.5, 0)
      .setDepth(851)
      .setScrollFactor(0);
    container.add(raceClassText);
    y += 38;

    // ── Subclase ──
    const sub = this.statsData.subclass || 'Sin subclase';
    const subText = this.scene.add
      .text(cx, y, sub, withGameFont({
        fontSize: '20px',
        color: '#555555',
      }))
      .setOrigin(0.5, 0)
      .setDepth(851)
      .setScrollFactor(0);
    container.add(subText);
    y += 28;

    // ── Ascendencia ──
    const anc = this.statsData.ancestry || 'Sin ascendencia';
    const ancText = this.scene.add
      .text(cx, y, anc, withGameFont({
        fontSize: '20px',
        color: '#555555',
      }))
      .setOrigin(0.5, 0)
      .setDepth(851)
      .setScrollFactor(0);
    container.add(ancText);
    y += 38;

    // ── HR (separador único, debajo de ascendencia) ──
    const sep = this.scene.add
      .rectangle(cx, y, width - 20, 1, 0x333333, 1)
      .setOrigin(0.5)
      .setDepth(851)
      .setScrollFactor(0);
    container.add(sep);
    y += 16;

    return y;
  }

  /**
   * Renderiza 4 slots de equipo a la izquierda, char preview 2x al centro,
   * y 4 slots a la derecha. Pobla los iconos vía `refreshEquipmentDisplay()`.
   */
  private buildEquipmentDisplay(
    container: Phaser.GameObjects.Container,
    cx: number,
    startY: number,
    _width: number
  ): void {
    const SLOT_SIZE = 40;
    const SLOT_GAP_Y = 6;
    const CHAR_W = 80;
    const SIDE_GAP = 16;
    const colHeight = SLOT_SIZE * 4 + SLOT_GAP_Y * 3;

    const leftSlots: EquipmentSlot[] = [
      EquipmentSlot.WEAPON,
      EquipmentSlot.SHIELD,
      EquipmentSlot.HEAD,
      EquipmentSlot.BODY,
    ];
    const rightSlots: EquipmentSlot[] = [
      EquipmentSlot.AMULET,
      EquipmentSlot.RING,
      EquipmentSlot.BACK,
      EquipmentSlot.BOOTS,
    ];

    const leftColCx = cx - CHAR_W / 2 - SIDE_GAP - SLOT_SIZE / 2;
    const rightColCx = cx + CHAR_W / 2 + SIDE_GAP + SLOT_SIZE / 2;

    leftSlots.forEach((slot, i) => {
      const slotY = startY + i * (SLOT_SIZE + SLOT_GAP_Y) + SLOT_SIZE / 2;
      this.buildEquipSlot(container, slot, leftColCx, slotY, SLOT_SIZE);
    });

    rightSlots.forEach((slot, i) => {
      const slotY = startY + i * (SLOT_SIZE + SLOT_GAP_Y) + SLOT_SIZE / 2;
      this.buildEquipSlot(container, slot, rightColCx, slotY, SLOT_SIZE);
    });

    // Slot de BARCO: noveno slot, centrado debajo del preview del personaje.
    // Ver docs/WATER_AND_BOATS.md.
    const boatSlotY = startY + colHeight + SLOT_GAP_Y + SLOT_SIZE / 2;
    this.buildEquipSlot(container, EquipmentSlot.BOAT, cx, boatSlotY, SLOT_SIZE);

    // Char preview container (centrado verticalmente con los slots).
    this.characterPreviewContainer = this.scene.add.container(cx, startY + colHeight / 2);
    this.characterPreviewContainer.setDepth(851);
    this.characterPreviewContainer.setScrollFactor(0);
    container.add(this.characterPreviewContainer);

    // Render inicial (icons + char sprite).
    this.refreshEquipmentDisplay();
  }

  /**
   * Crea un slot vacío (rect + placeholder traslúcido + icon oculto) y
   * registra hover/right-click. El contenido se pobla en `refreshEquipmentDisplay`.
   */
  private buildEquipSlot(
    parent: Phaser.GameObjects.Container,
    slot: EquipmentSlot,
    cx: number,
    cy: number,
    size: number
  ): void {
    const rect = this.scene.add
      .rectangle(cx, cy, size, size, 0x000000, 0.8)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x444444)
      .setDepth(851)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    parent.add(rect);

    const placeholderTex = getIconTexture('placeholder');
    const placeholder = placeholderTex && this.scene.textures.exists(placeholderTex.textureKey)
      ? this.scene.add
          .sprite(cx, cy, placeholderTex.textureKey, placeholderTex.frame)
          .setOrigin(0.5)
          .setDisplaySize(size * 0.5, size * 0.5)
          .setAlpha(0.18)
          .setDepth(852)
          .setScrollFactor(0)
      : null;
    if (placeholder) parent.add(placeholder);

    const icon = this.scene.add
      .sprite(cx, cy, 'item_icon_noicon')
      .setOrigin(0.5)
      .setDisplaySize(size * 0.7, size * 0.7)
      .setDepth(853)
      .setScrollFactor(0)
      .setVisible(false);
    parent.add(icon);

    this.equipmentSlots.set(slot, { rect, icon, placeholder, currentItem: null });

    // Hover → ItemTooltip si hay item.
    rect.on('pointerover', (pointer: Phaser.Input.Pointer) => {
      const data = this.equipmentSlots.get(slot);
      if (!data?.currentItem) return;
      const tooltip = this.ensureItemTooltip();
      tooltip.show(data.currentItem.itemId, pointer.x, pointer.y, {
        rolledRarity: data.currentItem.rolledRarity,
        rolledAffixes: data.currentItem.rolledAffixes,
      });
    });
    rect.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const data = this.equipmentSlots.get(slot);
      if (!data?.currentItem) return;
      const tooltip = this.ensureItemTooltip();
      if (tooltip.isShown()) {
        tooltip.show(data.currentItem.itemId, pointer.x, pointer.y, {
          rolledRarity: data.currentItem.rolledRarity,
          rolledAffixes: data.currentItem.rolledAffixes,
        });
      }
    });
    rect.on('pointerout', () => {
      this.itemTooltip?.hide();
    });

    // Right-click → unequip si hay item.
    rect.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.button !== 2) return;
      const data = this.equipmentSlots.get(slot);
      if (!data?.currentItem) return;
      this.itemTooltip?.hide();
      this.callbacks.onUnequip(slot);
      pointer.event.stopPropagation();
    });
  }

  private ensureItemTooltip(): ItemTooltip {
    if (!this.itemTooltip) {
      this.itemTooltip = new ItemTooltip(this.scene);
    }
    return this.itemTooltip;
  }

  /**
   * Recompone TODO el equipment display (slots + char preview).
   * Conveniencia para llamar después de un rebuild completo (updateUI).
   * Para refresh granular usar `refreshEquippedSlots()` o `refreshCharacterPreview()`.
   */
  refreshEquipmentDisplay(): void {
    this.refreshEquippedSlots();
    this.refreshCharacterPreview();
  }

  /**
   * Actualiza solo los iconos de los slots de equipment (basado en el inventory
   * snapshot vía callback). Llamar cuando llega INVENTORY_SYNC.
   */
  refreshEquippedSlots(): void {
    if (this.equipmentSlots.size === 0) return;
    const equipped = this.callbacks.getEquippedItems();
    this.equipmentSlots.forEach((data, slot) => {
      const item = equipped.get(slot) ?? null;
      data.currentItem = item;
      if (!item) {
        data.icon.setVisible(false);
        data.placeholder?.setVisible(true);
        return;
      }
      const tpl = getItemTemplate(item.itemId);
      if (!tpl) {
        data.icon.setVisible(false);
        data.placeholder?.setVisible(true);
        return;
      }
      const key = `item_icon_${tpl.icon}`;
      if (this.scene.textures.exists(key)) {
        data.icon.setTexture(key);
        data.icon.setVisible(true);
        data.placeholder?.setVisible(false);
      } else if (this.scene.textures.exists('item_icon_noicon')) {
        data.icon.setTexture('item_icon_noicon');
        data.icon.setVisible(true);
        data.placeholder?.setVisible(false);
      } else {
        data.icon.setVisible(false);
        data.placeholder?.setVisible(true);
      }
    });
  }

  /**
   * Re-renderiza el sprite del char preview (body + head + helm + boots + shield
   * + weapon) a partir de los visualIds del player local. Llamar cuando cambian
   * los visualIds en el PlayerState schema.
   */
  refreshCharacterPreview(): void {
    if (!this.characterPreviewContainer) return;
    this.renderCharacterPreview();
  }

  /**
   * Compone body + head + helm + boots + shield + weapon centrado en (0,0)
   * del `characterPreviewContainer`, a escala 2x. Se llama en cada refresh.
   */
  private renderCharacterPreview(): void {
    const container = this.characterPreviewContainer;
    if (!container) return;

    container.removeAll(true);

    const ids = this.callbacks.getLocalPlayerVisualIds();
    if (!ids) return;

    const SCALE = 2;
    const dir = Direction.DOWN;

    // Punto "pies" del personaje dentro del container. Lo centramos verticalmente
    // tomando el alto del body (BODY_FRAME_HEIGHT) escalado.
    const bodyBottomY = (CHAR_PREVIEW.BODY_FRAME_HEIGHT * SCALE) / 2;

    // Body
    const bodyKey = getBodySpriteKey(ids.bodyId);
    if (ids.bodyId > 0 && this.scene.textures.exists(bodyKey)) {
      const bodySprite = this.scene.add
        .sprite(0, bodyBottomY, bodyKey, getBodyIdleFrame(dir, ids.bodyId))
        .setOrigin(0.5, 1)
        .setScale(SCALE)
        .setScrollFactor(0)
        .setDepth(852);
      container.add(bodySprite);
    }

    // Head
    if (ids.headId > 0) {
      const headKey = getHeadSpriteKey(ids.headId);
      if (this.scene.textures.exists(headKey)) {
        const headCenterY =
          bodyBottomY -
          CHAR_PREVIEW.HEAD_OFFSET_FROM_BODY_BOTTOM * SCALE -
          (CHAR_PREVIEW.HEAD_FRAME_HEIGHT * SCALE) / 2;
        const headSprite = this.scene.add
          .sprite(0, headCenterY, headKey, getHeadFrameForDirection(dir))
          .setOrigin(0.5, 0.5)
          .setScale(SCALE)
          .setScrollFactor(0)
          .setDepth(853);
        container.add(headSprite);

        // Helm (8px arriba del head, escalado)
        if (ids.helmId > 0) {
          const helmKey = getHelmetSpriteKey(ids.helmId);
          if (this.scene.textures.exists(helmKey)) {
            const helmY = headCenterY + CHAR_PREVIEW.HELM_OFFSET_Y * SCALE;
            const helmSprite = this.scene.add
              .sprite(0, helmY, helmKey, getHelmetFrameForDirection(dir))
              .setOrigin(0.5, 0.5)
              .setScale(SCALE)
              .setScrollFactor(0)
              .setDepth(854);
            container.add(helmSprite);
          }
        }
      }
    }

    // Boots
    if (ids.bootId > 0) {
      const bootKey = getBootSpriteKey(ids.bootId);
      if (this.scene.textures.exists(bootKey)) {
        const bootSprite = this.scene.add
          .sprite(0, bodyBottomY, bootKey, getBootFrameForDirection(dir))
          .setOrigin(0.5, 1)
          .setScale(SCALE)
          .setScrollFactor(0)
          .setDepth(853);
        container.add(bootSprite);
      }
    }

    // Shield
    if (ids.shieldId > 0) {
      const shieldKey = getShieldSpriteKey(ids.shieldId);
      if (this.scene.textures.exists(shieldKey)) {
        const shieldSprite = this.scene.add
          .sprite(CHAR_PREVIEW.SHIELD_OFFSET_X * SCALE, bodyBottomY, shieldKey, getShieldIdleFrame(dir))
          .setOrigin(0.5, 1)
          .setScale(SCALE)
          .setScrollFactor(0)
          .setDepth(854);
        container.add(shieldSprite);
      }
    }

    // Weapon
    if (ids.weaponId > 0) {
      const weaponKey = getWeaponSpriteKey(ids.weaponId);
      if (this.scene.textures.exists(weaponKey)) {
        const weaponSprite = this.scene.add
          .sprite(CHAR_PREVIEW.WEAPON_OFFSET_X * SCALE, bodyBottomY, weaponKey, getWeaponIdleFrame(dir))
          .setOrigin(0.5, 1)
          .setScale(SCALE)
          .setScrollFactor(0)
          .setDepth(854);
        container.add(weaponSprite);
      }
    }
  }

  private buildRaceTooltip(raceName: string): string[] {
    const lines: string[] = [];
    lines.push(`Raza: ${raceName}`);
    
    // Convertir nombre de raza a ID
    let raceId: CharacterRaceId | null = null;
    const race = CHARACTER_RACES.find(r => r.labelEs === raceName);
    if (race) {
      raceId = race.id;
    }
    
    if (raceId) {
      const rows = describeModifiers(getRaceInnateModifiers(raceId));
      if (rows.length > 0) {
        lines.push('Beneficios:');
        rows.forEach((row) => {
          lines.push(`${row.label}: ${formatModifierValue(row)}`);
        });
      } else {
        lines.push('Sin beneficios especiales');
      }
    }

    return lines;
  }

  /**
   * Renderiza los grupos de stats avanzados (Velocidades / Críticos / Regen /
   * Combate Avanzado / Flags) dentro del scrollContent dado. Coords relativas
   * al top-left del scrollContent (0,0 = arriba a la izquierda).
   */
  private buildStatsColumn(container: Phaser.GameObjects.Container, _width: number): void {
    if (!this.statsData) return;

    const startX = 10;
    let cursorY = 10;
    // ×2 (legibilidad 2026-07-11): pitch de fila y gap de grupo escalados con las fuentes.
    const rowGap = 30;
    const groupGap = 16;

    const addTitle = (label: string): void => {
      const t = this.scene.add
        .text(startX, cursorY, label, withGameFont({
          fontSize: '24px',
          color: '#ffffff',
          fontStyle: 'bold',
        }))
        .setOrigin(0, 0)
        .setDepth(851)
        .setScrollFactor(0);
      container.add(t);
      cursorY += rowGap;
    };

    /**
     * Renderiza una fila: opcional sprite prefijo (si el modKey está mapeado
     * en `STAT_ICON_MAP`) + `label: value` + modificador opcional verde.
     * Si se pasan `tooltipLines`, hover sobre la fila muestra el desglose.
     */
    const addRow = (
      label: string,
      value: string,
      modifier?: string,
      modKey?: string,
      tooltipLines?: string[]
    ): void => {
      const ICON_SIZE = 22;
      const ICON_GAP = 8;
      let textX = startX + 10;
      if (modKey) {
        const iconName = getStatIconName(modKey);
        if (iconName) {
          const tex = getIconTexture(iconName);
          if (tex && this.scene.textures.exists(tex.textureKey)) {
            const sprite = this.scene.add
              .sprite(startX + 10, cursorY + ICON_SIZE / 2, tex.textureKey, tex.frame)
              .setOrigin(0, 0.5)
              .setDisplaySize(ICON_SIZE, ICON_SIZE)
              .setDepth(851)
              .setScrollFactor(0);
            container.add(sprite);
            textX = startX + 10 + ICON_SIZE + ICON_GAP;
          }
        }
      }
      // Label en negrita, value en normal (dos textos separados).
      const labelText = this.scene.add
        .text(textX, cursorY, `${label}: `, withGameFont({
          fontSize: '20px',
          color: '#cccccc',
          fontStyle: 'bold',
        }))
        .setOrigin(0, 0)
        .setDepth(851)
        .setScrollFactor(0);
      container.add(labelText);
      const valueText = this.scene.add
        .text(textX + labelText.width, cursorY, value, withGameFont({
          fontSize: '20px',
          color: '#cccccc',
        }))
        .setOrigin(0, 0)
        .setDepth(851)
        .setScrollFactor(0);
      container.add(valueText);
      let totalRowWidth = labelText.width + valueText.width;
      if (modifier) {
        const modText = this.scene.add
          .text(textX + totalRowWidth + 4, cursorY, modifier, withGameFont({
            fontSize: '20px',
            color: '#88cc88',
          }))
          .setOrigin(0, 0)
          .setDepth(851)
          .setScrollFactor(0);
        container.add(modText);
        totalRowWidth += 4 + modText.width;
      }

      // Hover tooltip con desglose (Base / Items / Total).
      // Hit area cubre toda la row de izq a der (desde el sprite/icono hasta el modText).
      if (tooltipLines && tooltipLines.length > 0) {
        const hitX = startX + 10;
        const hitW = (textX - hitX) + totalRowWidth;
        const hitArea = this.scene.add
          .rectangle(hitX, cursorY - 1, hitW, rowGap, 0x000000, 0)
          .setOrigin(0, 0)
          .setDepth(854)
          .setScrollFactor(0)
          .setInteractive({ useHandCursor: true });
        container.add(hitArea);

        hitArea.on('pointerover', (p: Phaser.Input.Pointer) => {
          this.showTooltip(p.x + 12, p.y + 12, tooltipLines);
        });
        hitArea.on('pointermove', (p: Phaser.Input.Pointer) => {
          if (this.currentTooltip) this.updateTooltipPosition(p.x, p.y);
        });
        hitArea.on('pointerout', () => {
          this.hideTooltip();
        });
      }

      cursorY += rowGap;
    };

    // Formatters
    const fmtFlat = (v: number): string => {
      if (Number.isInteger(v)) return String(v);
      return v.toFixed(2).replace(/\.?0+$/, '');
    };
    const fmtPct = (v: number): string => `${Math.round(v * 100)}%`;
    const modFlat = (modKey: string): string | undefined => {
      const m = this.statsData?.modifiers?.[modKey] ?? 0;
      if (m === 0) return undefined;
      const sign = m > 0 ? '+' : '';
      return `(${sign}${fmtFlat(m)})`;
    };
    const modPct = (modKey: string): string | undefined => {
      const m = this.statsData?.modifiers?.[modKey] ?? 0;
      if (m === 0) return undefined;
      const sign = m > 0 ? '+' : '';
      return `(${sign}${fmtPct(m)})`;
    };

    /**
     * Convierte un valor numérico a string firmado (+X / -X) usando el formatter dado.
     */
    const signed = (v: number, fmt: (n: number) => string): string => {
      const sign = v >= 0 ? '+' : '';
      return `${sign}${fmt(v)}`;
    };

    /**
     * Devuelve las contribuciones individuales al `modKey` dado desde TODAS las
     * fuentes que manda el server (raza, clase, cada item equipado, buffs, sets),
     * para que el tooltip muestre de dónde sale cada valor. La suma == modifiers[modKey].
     */
    const contribs = (modKey: string): Array<{ name: string; value: number }> => {
      const out: Array<{ name: string; value: number }> = [];
      const sources = this.statsData?.modifierSources ?? [];
      for (const src of sources) {
        const value = src.modifiers?.[modKey] ?? 0;
        if (value !== 0) out.push({ name: src.label, value });
      }
      return out;
    };

    /**
     * Arma las líneas de tooltip "Base + (una línea por item) + Total" para un stat.
     * Si no hay items contribuyentes, omite la sección (no muestra "Items: +0").
     */
    const tipFlat = (statName: string, total: number, modKey: string): string[] => {
      const mod = this.statsData?.modifiers?.[modKey] ?? 0;
      const base = total - mod;
      const lines: string[] = [statName, `Base: ${fmtFlat(base)}`];
      contribs(modKey).forEach((c) => lines.push(`${c.name}: ${signed(c.value, fmtFlat)}`));
      lines.push(`Total: ${fmtFlat(total)}`);
      return lines;
    };
    const tipPct = (statName: string, total: number, modKey: string): string[] => {
      const mod = this.statsData?.modifiers?.[modKey] ?? 0;
      const base = total - mod;
      const lines: string[] = [statName, `Base: ${fmtPct(base)}`];
      contribs(modKey).forEach((c) => lines.push(`${c.name}: ${signed(c.value, fmtPct)}`));
      lines.push(`Total: ${fmtPct(total)}`);
      return lines;
    };
    /** Tooltip para Vida/Maná/Stamina. Lista contribuciones flat directas al modKey. */
    const tipPool = (statName: string, max: number, base: number, modKey: string): string[] => {
      const lines: string[] = [statName, `Base: ${fmtFlat(base)}`];
      contribs(modKey).forEach((c) => lines.push(`${c.name}: ${signed(c.value, fmtFlat)}`));
      lines.push(`Total: ${fmtFlat(max)}`);
      return lines;
    };
    /** Tooltip para daño (min/max). Sin base diferenciada en el schema. */
    const tipDmg = (statName: string, total: number, modKey: string): string[] => {
      const mod = this.statsData?.modifiers?.[modKey] ?? 0;
      const base = total - mod;
      const lines: string[] = [statName, `Base: ${fmtFlat(base)}`];
      contribs(modKey).forEach((c) => lines.push(`${c.name}: ${signed(c.value, fmtFlat)}`));
      lines.push(`Total: ${fmtFlat(total)}`);
      return lines;
    };

    const s = this.statsData;

    // -------- General (Vida / Maná / Stamina + Regen + Movimiento) --------
    addTitle('General');
    addRow('Vida', `${Math.round(s.hp)}/${Math.round(s.maxHp)}`, undefined, 'hp', tipPool('Vida', s.maxHp, s.baseHp, 'hp'));
    addRow('Maná', `${Math.round(s.mana)}/${Math.round(s.maxMana)}`, undefined, 'mana', tipPool('Maná', s.maxMana, s.baseMana, 'mana'));
    addRow('Energía', `${Math.round(s.stamina)}/${Math.round(s.maxStamina)}`, undefined, 'stamina', tipPool('Energía', s.maxStamina, s.baseStamina, 'stamina'));
    addRow('Regen Vida', `${fmtFlat(s.regenHP)}/s`, modFlat('regenHP'), 'regenHP', tipFlat('Regen Vida', s.regenHP, 'regenHP'));
    addRow('Regen Maná', `${fmtFlat(s.regenMana)}/s`, modFlat('regenMana'), 'regenMana', tipFlat('Regen Maná', s.regenMana, 'regenMana'));
    addRow('Regen Energía', `${fmtFlat(s.regenStamina)}/s`, modFlat('regenStamina'), 'regenStamina', tipFlat('Regen Energía', s.regenStamina, 'regenStamina'));
    addRow('Vel. Movimiento', fmtFlat(s.moveSpeed), modFlat('moveSpeed'), 'moveSpeed', tipFlat('Vel. Movimiento', s.moveSpeed, 'moveSpeed'));
    cursorY += groupGap;

    // -------- Ataque --------
    addTitle('Ataque');
    addRow('Golpe Mínimo', fmtFlat(s.minDamage), modFlat('minDamage'), 'minDamage', tipDmg('Golpe Mínimo', s.minDamage, 'minDamage'));
    addRow('Golpe Máximo', fmtFlat(s.maxDamage), modFlat('maxDamage'), 'maxDamage', tipDmg('Golpe Máximo', s.maxDamage, 'maxDamage'));
    addRow('Vel. Ataque', fmtFlat(s.attackSpeed), modFlat('attackSpeed'), 'attackSpeed', tipFlat('Vel. Ataque', s.attackSpeed, 'attackSpeed'));
    const radioStr = `${s.attackWidth}×${s.attackLength}${s.attackBack > 0 ? ` (+${s.attackBack} atrás)` : ''}`;
    addRow('Radio de Ataque', radioStr, undefined, 'attackSpeed', [
      'Tiles que cubre el golpe cuerpo a cuerpo direccional:',
      'ancho (impar) × largo, + tiles hacia atrás.',
      'Depende del arma y de bonos de items/buffs.',
    ]);
    addRow('Energía por Golpe', fmtFlat(s.attackStaminaCost), undefined, 'stamina', [
      'Energía consumida por cada golpe cuerpo a cuerpo.',
    ]);
    addRow('Vel. Casteo', fmtFlat(s.castSpeed), modFlat('castSpeed'), 'castSpeed', tipFlat('Vel. Casteo', s.castSpeed, 'castSpeed'));
    // Alcance de Hechizos: bonus flat al rango máx / reducción del mín. "Sin límite"
    // si algún mod único (unboundSpellRange) elimina min y max.
    const spellRangeStr = s.unboundSpellRange > 0
      ? 'Sin límite'
      : (s.spellRangeBonus > 0 ? `+${s.spellRangeBonus} tiles` : '—');
    const spellRangeTip = s.unboundSpellRange > 0
      ? [
          'Alcance de Hechizos',
          'Tus hechizos no tienen distancia mínima ni máxima.',
          ...contribs('spellRangeBonus').map((c) => `${c.name}: ${signed(c.value, fmtFlat)}`),
        ]
      : [
          'Alcance de Hechizos',
          'Extiende el rango MÁXIMO y reduce el MÍNIMO de tus hechizos (tiles).',
          `Base: ${fmtFlat(0)}`,
          ...contribs('spellRangeBonus').map((c) => `${c.name}: ${signed(c.value, fmtFlat)}`),
          `Total: ${fmtFlat(s.spellRangeBonus)}`,
        ];
    addRow('Alcance de Hechizos', spellRangeStr, undefined, 'spellRangeBonus', spellRangeTip);
    addRow('Crítico', fmtPct(s.critChance), modPct('critChance'), 'critChance', tipPct('Crítico', s.critChance, 'critChance'));
    addRow('Mult. Crítico', fmtPct(s.critMultiplier), modPct('critMultiplier'), 'critMultiplier', tipPct('Mult. Crítico', s.critMultiplier, 'critMultiplier'));
    addRow('Robo Vida Físico', fmtPct(s.lifeStealPhysical), modPct('lifeStealPhysical'), 'lifeStealPhysical', tipPct('Robo Vida Físico', s.lifeStealPhysical, 'lifeStealPhysical'));
    addRow('Robo Vida Mágico', fmtPct(s.lifeStealMagic), modPct('lifeStealMagic'), 'lifeStealMagic', tipPct('Robo Vida Mágico', s.lifeStealMagic, 'lifeStealMagic'));
    cursorY += groupGap;

    // -------- Defensa --------
    addTitle('Defensa');
    addRow('Defensa Física', fmtFlat(s.physDef), modFlat('physDef'), 'physDef',
      tipFlat('Defensa Física', s.physDef, 'physDef'));
    addRow('Defensa Mágica', fmtFlat(s.magicDef), modFlat('magicDef'), 'magicDef',
      tipFlat('Defensa Mágica', s.magicDef, 'magicDef'));
    addRow('Evasión', fmtPct(s.evasion), modPct('evasion'), 'evasion', tipPct('Evasión', s.evasion, 'evasion'));
    addRow('Reflejo Físico', fmtPct(s.reflectPhysicalDamage), modPct('reflectPhysicalDamage'), 'reflectPhysicalDamage', tipPct('Reflejo Físico', s.reflectPhysicalDamage, 'reflectPhysicalDamage'));
    addRow('Reflejo Mágico', fmtPct(s.reflectMagicDamage), modPct('reflectMagicDamage'), 'reflectMagicDamage', tipPct('Reflejo Mágico', s.reflectMagicDamage, 'reflectMagicDamage'));
    addRow('Curación Recibida', fmtPct(s.healingReceived), modPct('healingReceived'), 'healingReceived', tipPct('Curación Recibida', s.healingReceived, 'healingReceived'));
    cursorY += groupGap;

    // -------- Misc --------
    addTitle('Misc');
    addRow('Curación Otorgada', fmtPct(s.healingOutput), modPct('healingOutput'), 'healingOutput', tipPct('Curación Otorgada', s.healingOutput, 'healingOutput'));
    addRow('Magic Find', fmtPct(s.magicFind), modPct('magicFind'), 'magicFind', tipPct('Magic Find', s.magicFind, 'magicFind'));
    cursorY += groupGap;

    // -------- Daño por Reino (sólo si hay algún bonus) --------
    const kingdomEntries = Object.entries(s.damageVsKingdom ?? {}).filter(([, v]) => v !== 0);
    if (kingdomEntries.length > 0) {
      addTitle('Daño por Reino');
      for (const [kid, v] of kingdomEntries) {
        addRow(getKingdomLabel(kid as KingdomId), fmtPct(v), undefined, 'magicFind', [
          `Daño vs ${getKingdomLabel(kid as KingdomId)}`,
          'Daño extra contra criaturas de este Reino,',
          'con cualquier tipo de ataque (cuerpo a cuerpo, a distancia o magia).',
        ]);
      }
      cursorY += groupGap;
    }

    // -------- Rareza por Reino (Magic Find condicional) --------
    const mfKingdomEntries = Object.entries(s.magicFindVsKingdom ?? {}).filter(([, v]) => v !== 0);
    if (mfKingdomEntries.length > 0) {
      addTitle('Rareza por Reino');
      for (const [kid, v] of mfKingdomEntries) {
        addRow(getKingdomLabel(kid as KingdomId), fmtPct(v), undefined, 'magicFind', [
          `Rareza vs ${getKingdomLabel(kid as KingdomId)}`,
          'Magic Find extra (chance de drops raros/épicos)',
          'sólo al matar criaturas de este Reino.',
        ]);
      }
      cursorY += groupGap;
    }

    // -------- Flags activos --------
    const anyFlag = s.noWalkNoise > 0 || s.noSpellWords > 0 || s.noStaminaCostPhysical > 0 || s.unboundSpellRange > 0;
    if (anyFlag) {
      addTitle('Flags Activos');
      if (s.noWalkNoise > 0) {
        // Sistema de Sonidos implementado: el mod suprime el SFX de pasos. Ver
        // docs/SOUND_AND_MUSIC.md (FootstepSystem gatea el sonido por noWalkNoise).
        addRow('Sigiloso al caminar', 'sí', undefined);
      }
      if (s.noSpellWords > 0) {
        addRow('Conjurador silencioso', 'sí', undefined);
      }
      if (s.noStaminaCostPhysical > 0) {
        addRow('Ataques sin energía', 'sí', undefined);
      }
      if (s.unboundSpellRange > 0) {
        addRow('Conjuro sin fronteras', 'sí', undefined, 'unboundSpellRange', [
          'Conjuro sin fronteras',
          'Tus hechizos no tienen distancia mínima ni máxima.',
          ...contribs('unboundSpellRange').map((c) => c.name),
        ]);
      }
    }
  }

  private updateUI(): void {
    if (!this.statsData || !this.panel) return;

    // El maskGraphics vive afuera de los containers (en world); hay que destruirlo aparte.
    if (this.rightScrollContainer) {
      this.rightScrollContainer.maskGraphics.destroy();
    }

    // Limpiar refs antes de destruir (los hijos serán destruidos por removeAll).
    this.leftPaneContainer = null;
    this.rightScrollContainer = null;
    this.rightScrollBounds = null;
    this.equipmentSlots.clear();
    this.characterPreviewContainer = null;

    this.sectionContainers.forEach(container => {
      container.removeAll(true);
    });

    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    this.createGeneralSection(panelX, panelY, panelWidth, panelHeight);
    // Forzar visibilidad explícita del sectionContainer + sus children recién
    // creados.
    const generalContainer = this.sectionContainers.get('general');
    if (generalContainer) {
      generalContainer.setVisible(true).setActive(true);
      generalContainer.iterate((child: Phaser.GameObjects.GameObject) => {
        (child as unknown as { setVisible: (v: boolean) => void }).setVisible(true);
      });
    }
  }

  /**
   * Rebuild parcial: sólo reconstruye la columna derecha (stats) sin tocar
   * el paper-doll de equipment + preview de la izquierda. Llamado al recibir
   * CHARACTER_STATS — evita destruir/recrear los sprites del preview (que
   * causaba glitch visual al equipar/desequipar items).
   *
   * Recrea el scroll container completo (con geometry mask fresca) en vez de
   * reusar el existente — mirror del path de `updateUI` (probado), pero scoped
   * a la columna derecha para no recrear el preview/slots de la izquierda.
   * Preserva el scroll actual para no esconder la sección modificada.
   */
  private rebuildStatsColumnOnly(): void {
    const generalContainer = this.sectionContainers.get('general');
    if (!this.statsData || !this.panel || !this.rightScrollContainer || !generalContainer) {
      // Si por algún motivo no hay container previo, caer al rebuild completo.
      this.updateUI();
      return;
    }

    // Recalcular dimensiones de la columna derecha (mismo cálculo que en
    // `createGeneralSection`).
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const contentWidth = panelWidth - 40;
    const startX = -panelWidth / 2 + 20;
    const COL_GAP = 20;
    const colWidth = (contentWidth - COL_GAP) / 2;
    const colHeight = panelHeight - CONTENT_TOP_OFFSET - 20;
    const contentStartScreenY = panelY - panelHeight / 2 + CONTENT_TOP_OFFSET;
    const rightX = startX + colWidth + COL_GAP;

    // Preservar el scroll actual para reaplicarlo tras recrear.
    const prevScrollY = this.rightScrollContainer.scrollY;

    // Destruir el scroll container viejo (mask vive en world, fuera del container).
    this.rightScrollContainer.maskGraphics.destroy();
    this.rightScrollContainer.container.destroy();

    // Recrear desde cero con mask fresca (mismo flujo que createGeneralSection).
    this.rightScrollContainer = this.createScrollContainer(generalContainer, rightX, 0, colWidth, colHeight);
    this.buildStatsColumn(this.rightScrollContainer.scrollContent, colWidth);
    this.refreshScrollMask(this.rightScrollContainer);
    this.rightScrollBounds = { x: panelX + rightX, y: contentStartScreenY, w: colWidth, h: colHeight };
    this.updateScrollLimits();

    // Reaplicar el scroll previo (scrollContainer clampa a maxScrollY).
    if (prevScrollY > 0) {
      this.scrollContainer(this.rightScrollContainer, prevScrollY);
    }
  }

  private hideTooltip(): void {
    if (this.currentTooltip) {
      this.currentTooltip.destroy();
      this.currentTooltip = null;
      this.currentTooltipContent = null;
    }
  }

  private updateTooltipPosition(x: number, y: number): void {
    if (this.currentTooltip) {
      // Actualizar posición del tooltip sin recrearlo (más eficiente)
      this.currentTooltip.setPosition(x + 12, y + 12);
    }
  }

  private showTooltip(x: number, y: number, content: string[]): void {
    this.hideTooltip();

    if (!this.panel || content.length === 0) return;

    // Ocultar la primera fila (nombre de la característica)
    const filteredContent = content.slice(1);
    if (filteredContent.length === 0) return;

    // Guardar el contenido filtrado para poder actualizar la posición
    this.currentTooltipContent = filteredContent;

    // Calcular ancho del tooltip basado en el contenido más largo
    let maxWidth = 0;
    filteredContent.forEach(line => {
      const width = line.length * 7; // Aproximación
      if (width > maxWidth) maxWidth = width;
    });
    maxWidth = Math.max(maxWidth, 180);
    maxWidth = Math.min(maxWidth, 250);

    const padding = 10;
    const lineHeight = 18;
    const height = filteredContent.length * lineHeight + padding * 2;

    // Crear container en la posición del mouse (como en ph-bh)
    const container = this.scene.add.container(x, y);
    container.setScrollFactor(0);
    container.setDepth(900);

    // Fondo en coordenadas relativas al container (0, 0)
    const tooltipBg = this.scene.add
      .rectangle(0, 0, maxWidth, height, 0x000000, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0x666666);
    container.add(tooltipBg);

    // Crear múltiples textos para poder aplicar colores
    let currentY = padding + lineHeight / 2;

    filteredContent.forEach((line, index) => {
      let color = '#ffffff';
      
      // Detectar si la línea tiene un valor positivo (verde) o negativo (rojo)
      if (line.includes('Raza ') && (line.includes('+') || line.includes('-'))) {
        // Modificador de raza: solo el número y símbolo deben tener color
        const match = line.match(/^(Raza .+?): ([+-])(\d+)$/);
        if (match) {
          const prefix = match[1] + ': ';
          const sign = match[2];
          const value = match[3];
          const numColor = sign === '+' ? '#00ff00' : '#ff0000';
          
          const prefixText = this.scene.add
            .text(padding, currentY, prefix, withGameFont({
              fontSize: '11px',
              color: '#ffffff',
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          const prefixWidth = prefixText.width;
          const numberText = this.scene.add
            .text(padding + prefixWidth, currentY, sign + value, withGameFont({
              fontSize: '11px',
              color: numColor,
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          container.add(prefixText);
          container.add(numberText);
          currentY += lineHeight;
          return;
        }
      } else if (line.includes('Ascendencia: +')) {
        // Solo el número y símbolo deben tener color
        const match = line.match(/^(Ascendencia): \+(\d+)$/);
        if (match) {
          const prefix = match[1] + ': ';
          const value = match[2];
          const numColor = '#00ff00';
          
          const prefixText = this.scene.add
            .text(padding, currentY, prefix, withGameFont({
              fontSize: '11px',
              color: '#ffffff',
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          const prefixWidth = prefixText.width;
          const numberText = this.scene.add
            .text(padding + prefixWidth, currentY, '+' + value, withGameFont({
              fontSize: '11px',
              color: numColor,
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          container.add(prefixText);
          container.add(numberText);
          currentY += lineHeight;
          return;
        }
      } else if (line.includes('Armadura: -')) {
        // Solo el número y símbolo deben tener color
        const match = line.match(/^(Armadura): -(\d+)$/);
        if (match) {
          const prefix = match[1] + ': ';
          const value = match[2];
          const numColor = '#ff0000';
          
          const prefixText = this.scene.add
            .text(padding, currentY, prefix, withGameFont({
              fontSize: '11px',
              color: '#ffffff',
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          const prefixWidth = prefixText.width;
          const numberText = this.scene.add
            .text(padding + prefixWidth, currentY, '-' + value, withGameFont({
              fontSize: '11px',
              color: numColor,
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          container.add(prefixText);
          container.add(numberText);
          currentY += lineHeight;
          return;
        }
      } else if (line.includes('Modificador:')) {
        // Para modificadores, solo el número debe tener color
        const match = line.match(/Modificador: ([+-])(\d+)/);
        if (match) {
          const sign = match[1];
          const value = match[2];
          const prefix = 'Modificador: ';
          const numColor = sign === '+' ? '#00ff00' : '#ff0000';
          
          // Crear texto con color solo en el número
          const prefixText = this.scene.add
            .text(padding, currentY, prefix, withGameFont({
              fontSize: '11px',
              color: '#ffffff',
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          const prefixWidth = prefixText.width;
          const numberText = this.scene.add
            .text(padding + prefixWidth, currentY, sign + value, withGameFont({
              fontSize: '11px',
              color: numColor,
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          container.add(prefixText);
          container.add(numberText);
          currentY += lineHeight;
          return;
        }
      } else if (line.includes('Items equipados:')) {
        // Para items equipados, solo el número y símbolo deben tener color
        const match = line.match(/^(Items equipados): ([+-])(\d+)$/);
        if (match) {
          const prefix = match[1] + ': ';
          const sign = match[2];
          const value = match[3];
          const numColor = sign === '+' && value !== '0' ? '#00ff00' : (sign === '-' ? '#ff0000' : '#ffffff');
          
          const prefixText = this.scene.add
            .text(padding, currentY, prefix, withGameFont({
              fontSize: '11px',
              color: '#ffffff',
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          const prefixWidth = prefixText.width;
          const numberText = this.scene.add
            .text(padding + prefixWidth, currentY, sign + value, withGameFont({
              fontSize: '11px',
              color: numColor,
              align: 'left',
            }))
            .setOrigin(0, 0.5);
          
          container.add(prefixText);
          container.add(numberText);
          currentY += lineHeight;
          return;
        }
      } else if (line.match(/^[^:]+: [+-][\d.]+%?$/) && !line.includes('Beneficios:')) {
        // Líneas como "Espada: +5", "Capa: -3", "Anillo: +5%", "Botas: +0.5"
        // — solo el número (con signo y opcional %/decimal) tiene color verde/rojo.
        const match = line.match(/^([^:]+): ([+-])([\d.]+%?)$/);
        if (match) {
          const prefix = match[1] + ': ';
          const sign = match[2];
          const value = match[3];
          const numColor = sign === '+' ? '#00ff00' : '#ff0000';

          const prefixText = this.scene.add
            .text(padding, currentY, prefix, withGameFont({
              fontSize: '11px',
              color: '#ffffff',
              align: 'left',
            }))
            .setOrigin(0, 0.5);

          const prefixWidth = prefixText.width;
          const numberText = this.scene.add
            .text(padding + prefixWidth, currentY, sign + value, withGameFont({
              fontSize: '11px',
              color: numColor,
              align: 'left',
            }))
            .setOrigin(0, 0.5);

          container.add(prefixText);
          container.add(numberText);
          currentY += lineHeight;
          return;
        }
      }

      const text = this.scene.add
        .text(padding, currentY, line, withGameFont({
          fontSize: '11px',
          color: color,
          align: 'left',
        }))
        .setOrigin(0, 0.5);
      container.add(text);
      currentY += lineHeight;
    });

    this.currentTooltip = container;
  }

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.bg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.titleIcon?.setVisible(visible).setActive(visible);
    panel.closeButton.setVisible(visible).setActive(visible);

    this.sectionContainers.forEach((container) => {
      container.setVisible(visible);
    });

    this.isOpen = visible;
    if (!visible) {
      this.hideTooltip();
      this.itemTooltip?.hide();
    }
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight } = panel;
    panel.bg.setPosition(newX, newY);
    panel.title.setPosition(newX, newY - panelHeight / 2 + 16);
    if (panel.titleIcon) {
      const ICON = 18;
      const GAP = 8;
      panel.titleIcon.setPosition(newX - panel.title.width / 2 - GAP - ICON / 2, panel.title.y);
    }
    panel.closeButton.setPosition(newX + panelWidth / 2 - 20, newY - panelHeight / 2 + 8);

    // Mover contenedores de secciones (la posición se calcula relativa al panel).
    const containerY = newY - panelHeight / 2 + CONTENT_TOP_OFFSET;
    this.sectionContainers.forEach((container) => {
      container.setPosition(newX, containerY);
    });

    // Recalcular bounds del wheel handler (la columna derecha se movió con el panel).
    if (this.rightScrollBounds && this.rightScrollContainer) {
      const COL_GAP = 20;
      const contentWidth = panelWidth - 40;
      const colWidth = (contentWidth - COL_GAP) / 2;
      const startX = -panelWidth / 2 + 20;
      const rightX = startX + colWidth + COL_GAP;
      this.rightScrollBounds = {
        x: newX + rightX,
        y: containerY + this.rightScrollContainer.container.y,
        w: colWidth,
        h: this.rightScrollBounds.h,
      };
      // Recalcular máscara: el container se movió, el clip rect también.
      this.refreshScrollMask(this.rightScrollContainer);
    }
  }
}